/**
 * Value-shape transforms applied to `_bind.*` lookup results.
 *
 * Pure functions — no React, no context, no I/O. Each transform is a
 * total function: it never throws, and degenerate inputs fall back to
 * safe empty values per ADR-11 (child SAD §4 Transform Table).
 */

import { isAffirmative } from '../affirmative';

/**
 * Companion values collected from sibling `_bind.*` props before a
 * transform is invoked. `optionLabelField` / `optionValueField` are
 * consumed by {@link applyOptions}; `tagKeys` / `defaultTagKeys` by
 * {@link applyTagLabels} and — when the `stats` input is a keyed object —
 * by {@link applyStats} to curate and order which entries become tiles.
 */
export interface TransformCompanions {
  readonly optionLabelField?: unknown;
  readonly optionValueField?: unknown;
  readonly tagKeys?: unknown;
  readonly defaultTagKeys?: unknown;
  readonly statLabelField?: unknown;
  readonly statValueField?: unknown;
  readonly statTooltipField?: unknown;
  readonly badgeField?: unknown;
}

/**
 * A single composite stat tile emitted by {@link applyStats}. Mirrors
 * the hen-ui `RankingCard` `StatField` shape (`stats` / `details` both
 * consume `StatField[]`). `tooltip` is optional and omitted entirely
 * when the source entry has no non-empty tooltip field.
 */
export interface StatField {
  readonly label: string;
  readonly value: string;
  readonly tooltip?: string;
}

/**
 * Map an array of arbitrary shapes into `{ label, value }` pairs for
 * HEN UI select/radio/checkbox components.
 *
 * Algorithm:
 *   1. Non-array input → `[]`.
 *   2. Deep-flatten array-of-arrays input (see {@link flattenForOptions}).
 *   3. For each leaf entry:
 *        label = entry[optionLabelField] ?? String(entry)
 *        value = entry[optionValueField] ?? entry.value ?? String(entry)
 *      where the companion field names are taken from the sibling
 *      `_bind.optionLabelField` / `_bind.optionValueField` declarations.
 *
 * When an entry is `null`/`undefined`, the coerced label/value is
 * `String(entry)` (`'null'` / `'undefined'`) — not dropped — so the
 * consumer can detect bad data without a silent empty list.
 *
 * An entry whose resolved VALUE is empty or whitespace-only is the one
 * exception, and is dropped with a warning. A Radix `Select.Item` throws on an
 * empty value rather than rendering, so keeping it would take the surrounding
 * page down instead of showing bad data — and the warning still says the data
 * was bad. Values are otherwise passed through untrimmed: a bound option value
 * is a query key, and silently rewriting one produces a filter that matches
 * nothing rather than a cosmetic difference.
 */
export function applyOptions(
  value: unknown,
  companions: TransformCompanions,
): ReadonlyArray<{ label: string; value: string }> {
  if (!Array.isArray(value)) {
    return [];
  }
  const labelField = coerceCompanion(
    companions.optionLabelField,
    'optionLabelField',
  );
  const valueField = coerceCompanion(
    companions.optionValueField,
    'optionValueField',
  );

  const items = flattenForOptions(value);

  const out: Array<{ label: string; value: string }> = [];
  let dropped = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const optionValue = resolveValue(item, valueField);
    if (optionValue.trim().length === 0) {
      dropped += 1;
      continue;
    }
    out.push({
      label: resolveLabel(item, labelField),
      value: optionValue,
    });
  }
  if (dropped > 0) {
    warnOnce(
      `[binding-enhancer] Dropped ${dropped} option(s) with an empty value; a select item cannot carry one.`,
    );
  }
  return out;
}

/**
 * Recursively flatten any nested-array structure into a flat list of
 * leaves. The `options` transform's authoring intent is always "a
 * flat collection of choices a user can pick" — never a tree of
 * sub-arrays.
 *
 * Why deep-flatten here (and not in `getByPath`):
 *   `getByPath` deliberately preserves array-of-arrays shape for
 *   `[*]` paths (`getByPath.ts` §"strict one-level flatten") so
 *   POST-body serialization can keep groupings recoverable. But the
 *   path syntax has no `[**]` deep-flatten selector and no indexing
 *   (`[0]`), so an author binding to e.g.
 *   `degrees[*].categories[*]` against
 *   `{ degrees: [{ categories: [{...},{...}] }] }` gets back
 *   `[[{...},{...}]]` — one wrapper per outer `[*]` whose inner
 *   value is itself an array. The Select then renders the entire
 *   inner array as a single `[object Object],…` option.
 *
 *   `options` is the only transform where this never makes sense
 *   (you cannot render an array as a single choice), so the
 *   unwrapping is safe to do here without breaking the general
 *   path-shape contract.
 *
 * Implementation note: explicit recursion + a single output array
 * avoids `Array.prototype.flat(Infinity)`'s shallow-copy cost on
 * every level. Object entries are emitted as-is — the recursion
 * stops at the first non-array node, so `{ items: [1,2] }` is one
 * leaf, not two.
 */
function flattenForOptions(value: ReadonlyArray<unknown>): unknown[] {
  const out: unknown[] = [];
  const visit = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) visit(v[i]);
      return;
    }
    out.push(v);
  };
  visit(value);
  return out;
}

/**
 * Companion fields are sibling `_bind.optionLabelField` /
 * `_bind.optionValueField` props that authors set as a string field
 * name. Anything other than a non-empty string falls back to the
 * default extraction (`String(item)` / `item.value`) and emits a
 * single warn-log per companion name so DSL authors can spot the
 * misconfiguration without diffing prop bags.
 */
function coerceCompanion(
  raw: unknown,
  companionName:
    | 'optionLabelField'
    | 'optionValueField'
    | 'statLabelField'
    | 'statValueField'
    | 'statTooltipField'
    | 'badgeField',
): string | undefined {
  if (typeof raw === 'string' && raw.length > 0) {
    return raw;
  }
  if (raw === undefined) {
    return undefined;
  }
  warnOnce(
    `[binding-enhancer] _bind.${companionName} expected a non-empty string field name; got ${describeCompanionType(
      raw,
    )}. Falling back to default value extraction — bound options may render as "[object Object]".`,
  );
  return undefined;
}

function describeCompanionType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  if (typeof value === 'object') return 'an object';
  return typeof value;
}

/**
 * Read a companion-named field off an entry and coerce it to a string.
 * Returns `undefined` when no field name is given, the entry is not an
 * indexable object, or the field is absent / `null` / `undefined` — the
 * shared "read a `_bind.<field>` off this entry" primitive behind the
 * options / stats / badges field-mapping.
 */
function readStringField(
  entry: unknown,
  field: string | undefined,
): string | undefined {
  if (field && isIndexableObject(entry)) {
    const raw = entry[field];
    if (raw !== undefined && raw !== null) {
      return String(raw);
    }
  }
  return undefined;
}

function resolveLabel(item: unknown, labelField: string | undefined): string {
  return readStringField(item, labelField) ?? String(item);
}

function resolveValue(item: unknown, valueField: string | undefined): string {
  const mapped = readStringField(item, valueField);
  if (mapped !== undefined) {
    return mapped;
  }
  if (
    isIndexableObject(item) &&
    item.value !== undefined &&
    item.value !== null
  ) {
    return String(item.value);
  }
  return String(item);
}

function isIndexableObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Coerce a `_bind.*` lookup result into a displayable string.
 *
 *   - array → entries (skipping `null` / `undefined`) joined by `', '`
 *   - `null` / `undefined` → `''`
 *   - object → `JSON.stringify(value)` (falls back to `''` on cycles)
 *   - scalar → `String(value)`
 */
export function applyText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== null && entry !== undefined)
      .map((entry) => applyText(entry))
      .join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value) ?? '';
    } catch {
      return '';
    }
  }
  return String(value);
}

/**
 * Return the array length, or `0` for non-array inputs. Matches the
 * ADR-11 transform table — empty / missing paths produce `0` so result
 * lists and skeletons can render identically to an empty list.
 */
export function applyCount(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/**
 * Coerce a `_bind.*` lookup result into a boolean "is empty" answer.
 *
 * Returns `true` for inputs that authors would naturally describe as
 * "not yet provided":
 *   - `undefined`, `null`
 *   - empty string `''`
 *   - empty array `[]`
 *   - plain object with no own enumerable keys `{}`
 *
 * Everything else (non-empty strings, numbers including `0`, booleans
 * including `false`, populated arrays / objects, dates, …) returns
 * `false`. `0` and `false` are intentionally NOT treated as empty — a
 * selected radio option with value `0` or a toggled-off boolean field
 * are still real user-provided values and should not register as
 * "missing" for gating purposes.
 *
 * The empty-object branch is restricted to **plain objects** (prototype
 * is `Object.prototype` or `null`). Non-plain objects — `Date`, `Map`,
 * `Set`, `URL`, class instances, and the like — represent host-supplied
 * runtime values and always return `false` regardless of whether they
 * happen to expose own enumerable keys. This avoids a footgun where a
 * `source: 'context'` value such as `new Date()` would otherwise be
 * misclassified as empty because built-in instances expose no own
 * enumerable fields.
 *
 * Pairs with the reserved `source: 'formState' | 'context'` shorthand
 * so DSL authors can express e.g. `_bind.disabled = isEmpty(formState.degree)`
 * without an inline ternary or comparison helper:
 *
 * ```jsonc
 * "_bind.disabled": {
 *   "source": "formState",
 *   "path":   "degree",
 *   "transform": "isEmpty"
 * }
 * ```
 */
export function applyIsEmpty(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      return false;
    }
    for (const key in value as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        return false;
      }
    }
    return true;
  }
  return false;
}

/**
 * Map an object of `{ label, value }` entries (e.g. offerings
 * `institutionalStats`, keyed by metric slug) into the array of labels
 * whose value is affirmative — the shape tag/pill props such as
 * `EditorialListing.tags` consume.
 *
 * "Affirmative" mirrors legacy Super Sonic `schoolDataPills`'
 * `isAffirmative`: boolean `true`, or the strings `'true'` / `'yes'`
 * (case-insensitive, trimmed). Numbers, other strings (`'No'`,
 * `'regional'`, …), and missing values are not affirmative, so stat
 * maps that mix booleans with display strings emit only the genuine
 * yes-signals.
 *
 * Companion `_bind.tagKeys` (array of entry-key strings): when present
 * and non-empty, only those keys are considered, **in array order** —
 * authors curate which stats become pills and their display priority
 * per config.
 *
 * Companion `_bind.defaultTagKeys` (array of entry-key strings): the
 * fallback key list used when `tagKeys` resolves to an empty list. This
 * supports a filter-aware binding where `tagKeys` is bound to the active
 * filter selection (`formState`): pre-interaction the selection is empty
 * so the bounded `defaultTagKeys` set shows; once the user selects
 * filters, only the selected keys' tags render. When BOTH `tagKeys` and
 * `defaultTagKeys` are absent/empty, every affirmative entry is emitted
 * in object-key order (legacy behavior).
 *
 * Total per ADR-11: non-object input → `[]`; entries that are not
 * `{ label, value }`-shaped or lack a non-empty string `label` are
 * skipped.
 */
export function applyTagLabels(
  value: unknown,
  companions: TransformCompanions,
): string[] {
  if (!isIndexableObject(value)) {
    return [];
  }
  const keys =
    coerceTagKeys(companions.tagKeys) ??
    coerceTagKeys(companions.defaultTagKeys) ??
    Object.keys(value);
  const out: string[] = [];
  for (const key of keys) {
    const entry = value[key];
    if (!isIndexableObject(entry)) {
      continue;
    }
    if (!isAffirmative(entry.value)) {
      continue;
    }
    if (typeof entry.label === 'string' && entry.label.length > 0) {
      out.push(entry.label);
    }
  }
  return out;
}

/**
 * Coerce the `_bind.tagKeys` companion into a key list. A valid array
 * keeps its non-empty-string entries in order (non-string entries are
 * dropped with a warn). An EMPTY array — authored as `[]` or emptied by
 * the filter — means "no curation" and falls back to all affirmative
 * entries, same as omitting the companion entirely. Non-array values
 * also fall back, with a single warn so authors can spot the bad
 * companion.
 */
function coerceTagKeys(raw: unknown): string[] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    const keys = raw.filter(
      (key): key is string => typeof key === 'string' && key.length > 0,
    );
    if (keys.length < raw.length) {
      warnOnce(
        `[binding-enhancer] _bind.tagKeys contained ${
          raw.length - keys.length
        } non-string entr(y/ies); they were ignored.`,
      );
    }
    return keys.length > 0 ? keys : undefined;
  }
  warnOnce(
    `[binding-enhancer] _bind.tagKeys expected an array of entry-key strings; got ${describeCompanionType(
      raw,
    )}. Falling back to all affirmative entries.`,
  );
  return undefined;
}

/**
 * Map arbitrary shapes into `{ label, value, tooltip? }` stat tiles — the
 * {@link StatField} shape hen-ui `RankingCard.stats` and `.details`
 * consume.
 *
 * Accepted inputs:
 *   - **Array** (e.g. a `[*]` path): used verbatim, one entry per element.
 *     Unlike `options` there is no deep-flatten — a nested array is not a
 *     meaningful stat tile.
 *   - **Keyed object** (e.g. offerings `institutionalStats`, keyed by
 *     metric slug → `{ label, value }`): normalized to an ordered entry
 *     list via {@link collectStatEntries}. The `tagKeys` companion curates
 *     and orders which keys become tiles (falling back to `defaultTagKeys`,
 *     then object-key order) — the same curation model as
 *     {@link applyTagLabels}, matching legacy's fixed, hand-ordered stat
 *     set. Keys absent from the object are skipped.
 *   - Anything else → `[]` (total, per ADR-11).
 *
 * Per surviving entry (nullish entries dropped, matching
 * {@link applyBadges}), fields are mapped via companion field names
 * (mirrors `optionLabelField` / `optionValueField`):
 *   label   = entry[statLabelField]   (fallback `String(entry)`)
 *   value   = entry[statValueField]   (fallback `''`)
 *   tooltip = entry[statTooltipField] when present & non-empty
 *
 * `label` / `value` are coerced with `String(...)`; `tooltip` is included
 * only when it resolves to a non-empty string. Entries that yield an empty
 * `label` are skipped — a stat tile with no label cannot render.
 */
export function applyStats(
  value: unknown,
  companions: TransformCompanions,
): StatField[] {
  const entries = collectStatEntries(value, companions);
  if (entries.length === 0) {
    return [];
  }
  const labelField = coerceCompanion(
    companions.statLabelField,
    'statLabelField',
  );
  const valueField = coerceCompanion(
    companions.statValueField,
    'statValueField',
  );
  const tooltipField = coerceCompanion(
    companions.statTooltipField,
    'statTooltipField',
  );

  const out: StatField[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry === null || entry === undefined) {
      continue;
    }
    const label = readStringField(entry, labelField) ?? String(entry);
    if (label.length === 0) {
      continue;
    }
    const statValue = readStringField(entry, valueField) ?? '';
    const tooltip = resolveStatTooltip(entry, tooltipField);
    out.push(
      tooltip === undefined
        ? { label, value: statValue }
        : { label, value: statValue, tooltip },
    );
  }
  return out;
}

/**
 * Normalize the `stats` input into an ordered entry list.
 *
 *   - Array → returned as-is.
 *   - Plain / indexable object → `Object.values` in a curated order: the
 *     `tagKeys` companion (then `defaultTagKeys`, then object-key order)
 *     selects and orders keys, exactly like {@link applyTagLabels}. Only
 *     own keys present on the object are emitted (a curated key that is
 *     missing is skipped rather than pushing `undefined`).
 *   - Anything else → `[]`.
 */
function collectStatEntries(
  value: unknown,
  companions: TransformCompanions,
): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (!isIndexableObject(value)) {
    return [];
  }
  const keys =
    coerceTagKeys(companions.tagKeys) ??
    coerceTagKeys(companions.defaultTagKeys) ??
    Object.keys(value);
  const out: unknown[] = [];
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      continue;
    }
    out.push(value[key]);
  }
  return out;
}

function resolveStatTooltip(
  entry: unknown,
  field: string | undefined,
): string | undefined {
  const tooltip = readStringField(entry, field);
  return tooltip !== undefined && tooltip.length > 0 ? tooltip : undefined;
}

/**
 * Map an array into a plain `string[]` of display badges — the shape
 * hen-ui `RankingCard.badges` consumes.
 *
 * Algorithm:
 *   1. Non-array input → `[]` (total, per ADR-11).
 *   2. Optional companion `_bind.badgeField`: when set and an entry is
 *      an indexable object, use `String(entry[badgeField])`; otherwise
 *      `String(entry)`.
 *   3. Drop `null` / `undefined` entries and entries that coerce to an
 *      empty string, so a sparse array does not render blank pills.
 *
 * Distinct from `tagLabels` (object-of-`{ label, value }` → affirmative
 * labels) and `text` (array → single joined string): `badges` keeps the
 * array shape as display strings.
 */
export function applyBadges(
  value: unknown,
  companions: TransformCompanions,
): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const badgeField = coerceCompanion(companions.badgeField, 'badgeField');

  const out: string[] = [];
  for (let i = 0; i < value.length; i++) {
    const entry = value[i];
    if (entry === null || entry === undefined) {
      continue;
    }
    const badge =
      badgeField && isIndexableObject(entry)
        ? (readStringField(entry, badgeField) ?? '')
        : String(entry);
    if (badge.length === 0) {
      continue;
    }
    out.push(badge);
  }
  return out;
}

const warnedMessages = new Set<string>();

function warnOnce(message: string): void {
  if (warnedMessages.has(message)) {
    return;
  }
  warnedMessages.add(message);
  // eslint-disable-next-line no-console
  console.warn(message);
}

/** Test-only reset for the warn-once ledger. */
export function __resetTransformWarnings(): void {
  warnedMessages.clear();
}
